import React, { useEffect, useState } from 'react'
import useIntelligenceSearch from 'common/providers/useIntelligenceSearch'
import useLegalChecker from 'common/providers/useLegalChecker'
import LoadingSpinner from 'cwp-components/base/LoadingSpinner'
import { LegalServices } from '../../icons/LegalServices'
import { Remedy } from '../../icons/Remedy'
import { Role } from '../../icons/Role'
import { Status } from '../../icons/Status'
import { useNavigate } from 'react-router-dom'
import { useAreaOfLawClassified } from 'common/hooks/useAreaOfLawClassified'
import useAreaOfLaw from 'common/providers/useAreaOfLaw'

const getIconComponent = (type: string) => {
  switch (type) {
    case 'legal_service':
      return <LegalServices />
    case 'remedy':
      return <Remedy />
    case 'role':
      return <Role />
    case 'status':
      return <Status />
    default:
      return null
  }
}

const FindALawyerIntelligence = () => {
  const navigate = useNavigate()
  const { areaOfLaw, getAreaOfLaw } = useAreaOfLaw()
  const [selectedTiles, setSelectedTiles] = useState<any>([])

  const { searchResult, createSearch, searchResultLoading } =
    useIntelligenceSearch()

  const { legalCheckerRRS, legalCheckerRRSLoading, getLegalCheckerRRS } =
    useLegalChecker()

  useEffect(() => {
    getLegalCheckerRRS()
    getAreaOfLaw({})
  }, [getLegalCheckerRRS, getAreaOfLaw])

  const { getAreaOfLawClassified } = useAreaOfLawClassified()

  const handleTileSection = (type: string, idx: number) => {
    setSelectedTiles((prev) => {
      // If the user is deselecting the current type
      if (prev[type] === idx) {
        const newState = { ...prev }
        delete newState[type]
        return newState
      }
      return { ...prev, [type]: idx }
    })
  }

  const onContinue = async () => {
    if (Object.keys(selectedTiles).length > 2) {
      try {
        const areaOfLawClassified = await getAreaOfLawClassified(searchResult.expanded_query)
        const matchedArea = areaOfLaw.find(
          (area) => area.master_term.toLowerCase() === areaOfLawClassified,
        )
        if (matchedArea) {
          navigate(
            `/find-a-lawyer/search?area_ids=${encodeURIComponent(
              matchedArea.id,
            )}`,
          )
        } else {
          throw new Error('No matching area of law found')
        }
      } catch (error) {
        console.error('Error fetching area of law:', error)
      }
    }
  }

  const [searchResultTextField, setSearchResultTextField] = useState(
    searchResult?.expanded_query,
  )

  const handleRetry = async () => {
    if (!searchResultTextField?.trim()) {
      alert('Please enter a description')
      return
    }
    try {
      await createSearch({ query: searchResultTextField })
    } catch (error) { }
  }
  useEffect(() => {
    if (searchResult?.expanded_query) {
      setSearchResultTextField(searchResult.expanded_query)
    }
  }, [searchResult])

  return (
    <div className='bg-[rgba(235,243,255,0.50)]'>
      <div className='md:p-[100px_242px_50px]  p-[16px_12px]'>
        <h1 className='text-center md:text-[18px] text-[14px] font-bold text-[#093269] md:mb-[24px] mb-[16px] '>
          We have expanded on what you have told us.
        </h1>
        <div className='md:p-[30px] p-[20px]   mx-auto md:text-[16px] !text-[14px] rounded-[8px] bg-white shadow-[0px_1px_12px_0px_rgba(0,0,0,0.12)] '>
          <textarea
            placeholder='Tell us what has brought you here today...'
            className='w-full max-sm:h-[550px] p-[20px] bg-[#F4F4F4] text-[#586885] rounded-[8px] border-[0.4px] hover:border-[2px] appearance-none focus:border-[2px] !focus:border-[#001A3D] hover:placeholder:text-[#83A7D7] hover:border-[#001A3D] border-[#9FB7D7] '
            rows={7}
            onChange={(e) => setSearchResultTextField(e.target.value)}
            value={searchResultTextField}
          ></textarea>
          <div className='flex  md:mt-[40px] mt-[20px]  justify-end text-[#405985] md:gap-[20px] md:items-center '>
            <p className='md:text-[14px] text-[12px]'>
              Directly edit the text if inaccurate or click ‘retry’ to redraft.
            </p>
            <button
              className='btn max-sm:mt-[30px] md:!text-[14px] !text-[12px] btn-default text-[#1C7D93] '
              onClick={handleRetry}
            >
              {searchResultLoading ? 'Retrying...' : 'Retry'}
            </button>
          </div>
        </div>
      </div>
      <div className='md:p-[50px_48px] p-[16px_12px] h-full relative'>
        <h5 className='text-center md:text-[18px] text-[14px] text-[#093269] mb-[24px] font-bold '>
          Select 3 options relevant to the above.{' '}
        </h5>
        {legalCheckerRRSLoading ? (
          <div className='flex justify-center items-center w-full h-full'>
            <LoadingSpinner />
          </div>
        ) : (
          <div className='grid md:grid-cols-5 max-sm:space-y-[16px] auto-rows-fr md:gap-[12px]'>
            {legalCheckerRRS?.categories.map((result, idx) => (
              <div
                key={idx}
                onClick={() => handleTileSection(result.type, idx)}
                className={`md:p-[24px] p-[16px] max-sm:space-y-[8px] text-[#586885] rounded-[12px] ${selectedTiles[result.type] === idx
                  ? 'opacity-50 cursor-pointer'
                  : !(result.type in selectedTiles)
                    ? 'bg-white hover:bg-gradient-to-r from-[#fff] from-[0%] to-[#D0E2FA] cursor-pointer'
                    : 'bg-white'
                  }`}
              >
                {getIconComponent(result.type)}
                <h6 className='md:text-[18px] text-[14px] font-bold  '>
                  {result.title}
                </h6>
                <p className='md:text-[16px] text-[12px] text-justify leading-[24px] tracking-[0.4px] my-[8px]  '>
                  {result.description}
                </p>
              </div>
            ))}
          </div>
        )}
        <div className='flex justify-end mt-[24px] md:p-[20px] p-[16px] bg-white rounded-[8px] sticky bottom-[20px] z-[10]'>
          <button
            onClick={onContinue}
            className={`btn btn-default ${Object.keys(selectedTiles).length > 2
              ? 'text-[#1C7D93]'
              : ' text-[#A0ABBE] cursor-not-allowed'
              }  `}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  )
}

export default FindALawyerIntelligence
